'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  Switch,
  Input,
  Button,
  Field,
  Accordion,
  ToggleGroup,
  Select,
  createSelectListCollection,
} from '@terra/ui-design-system';
import {
  ArrowRight02Icon,
  Tick01Icon,
  ArrowDown01Icon,
  ArrowUpDownIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import {
  HTTP_METHODS,
  MATCH_TYPE_LABELS,
  RULE_EDITOR_CONTROL_HEIGHT,
  getMatchPatternFieldConfig,
  getMatchTypesForLocation,
  getReplaceFieldConfig,
  getVisibleMatchTypeCategories,
  suggestRuleName,
  type RuleFormMatchType,
  type RuleFormType,
} from './match-replace-rule-form';
import { RuleWhereSection } from './RuleWhereSection';

type InlineRuleEditorProps = {
  ruleForm: RuleFormType;
  setRuleForm: React.Dispatch<React.SetStateAction<RuleFormType>>;
  isEditing: boolean;
  onSave: () => void;
  onCancel: () => void;
};
type RuleEditorSectionProps = {
  title: string;
  children: React.ReactNode;
};

function RuleEditorSection({ title, children }: RuleEditorSectionProps) {
  return (
    <VStack gap="1.5" alignItems="flex-start" w="full">
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="fg.subtle"
        textTransform="uppercase"
        letterSpacing="0.04em"
      >
        {title}
      </Text>
      {children}
    </VStack>
  );
}

export function InlineRuleEditor({
  ruleForm,
  setRuleForm,
  isEditing,
  onSave,
  onCancel,
}: InlineRuleEditorProps) {
  const tRuleDialog = useTranslations('matchReplace.ruleDialog');
  const tCommon = useTranslations('common');
  const visibleCategories = useMemo(
    () => getVisibleMatchTypeCategories(ruleForm.matchLocation),
    [ruleForm.matchLocation],
  );
  const matchTypeSelectCollection = useMemo(
    () =>
      createSelectListCollection({
        items: getMatchTypesForLocation(ruleForm.matchLocation).map((type) => ({
          label: MATCH_TYPE_LABELS[type],
          value: type,
        })),
      }),
    [ruleForm.matchLocation],
  );
  const matchPatternConfig = getMatchPatternFieldConfig(ruleForm.matchType);
  const replaceFieldConfig = getReplaceFieldConfig(ruleForm.matchType);
  const suggestedName = suggestRuleName(ruleForm);
  const showHeaderOrParamField =
    ruleForm.matchType === 'header' || ruleForm.matchType === 'paramValue';
  const handleMatchTypeChange = useCallback(
    (matchType: RuleFormMatchType) => {
      setRuleForm((current) => ({
        ...current,
        matchType,
      }));
    },
    [setRuleForm],
  );
  const handleMatchPatternChange = useCallback(
    (matchPattern: string) => {
      setRuleForm((current) => ({
        ...current,
        matchPattern,
      }));
    },
    [setRuleForm],
  );

  return (
    <VStack h="full" w="full" gap="0" overflow="hidden" bg="bg.default">
      <Box
        px="3"
        py="2"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        bg="bg.subtle"
        flexShrink={0}
        w="full"
      >
        <VStack gap="2" alignItems="flex-start" w="full">
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <VStack gap="0.5" alignItems="flex-start">
              <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                {isEditing ? tRuleDialog('editTitle') : tRuleDialog('newTitle')}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {tRuleDialog('subtitle')}
              </Text>
            </VStack>
            <HStack gap="2" alignItems="center" flexShrink={0}>
              <Text fontSize="xs" color="fg.muted">
                {tRuleDialog('ruleEnabled')}
              </Text>
              <Switch
                size="sm"
                checked={ruleForm.enabled}
                onCheckedChange={(details) =>
                  setRuleForm({ ...ruleForm, enabled: details.checked })
                }
              />
            </HStack>
          </HStack>
        </VStack>
      </Box>

      <Box flex="1" overflowY="auto" w="full" px="3" py="3">
        <VStack gap="4" alignItems="flex-start" w="full">
          <RuleEditorSection title={tRuleDialog('sectionWhere')}>
            <RuleWhereSection ruleForm={ruleForm} setRuleForm={setRuleForm} />
          </RuleEditorSection>

          <RuleEditorSection title={tRuleDialog('sectionWhat')}>
            <Select.Root
              collection={matchTypeSelectCollection}
              value={[ruleForm.matchType]}
              positioning={{ strategy: 'fixed', sameWidth: true }}
              onValueChange={(details) => {
                const value = details.value[0] as RuleFormMatchType | undefined;

                if (value) {
                  handleMatchTypeChange(value);
                }
              }}
            >
              <Select.Control w="full">
                <Select.Trigger h={RULE_EDITOR_CONTROL_HEIGHT} w="full">
                  <Select.ValueText
                    fontSize="xs"
                    placeholder={tRuleDialog('chooseWhatToMatch')}
                  />
                  <Select.Indicator>
                    <ArrowUpDownIcon size={12} />
                  </Select.Indicator>
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content maxH="240px" overflowY="auto">
                  {visibleCategories.map((category) => (
                    <Select.ItemGroup key={category.id}>
                      <Select.ItemGroupLabel fontSize="2xs">
                        {category.label}
                      </Select.ItemGroupLabel>
                      {category.types.map((matchType) => (
                        <Select.Item key={matchType} item={matchType}>
                          <Select.ItemText fontSize="xs">
                            {MATCH_TYPE_LABELS[matchType]}
                          </Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={10} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </RuleEditorSection>

          <RuleEditorSection title={tRuleDialog('sectionFindReplace')}>
            <Box
              w="full"
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              bg="bg.subtle"
              p="2.5"
            >
              <VStack gap="2.5" alignItems="stretch" w="full">
                {showHeaderOrParamField ? (
                  <Field.Root w="full">
                    <Field.Label fontSize="xs" color="fg.muted">
                      {ruleForm.matchType === 'header'
                        ? tRuleDialog('headerNameOptional')
                        : tRuleDialog('paramNameOptional')}
                    </Field.Label>
                    <Input
                      size="xs"
                      h={RULE_EDITOR_CONTROL_HEIGHT}
                      fontSize="xs"
                      bg="bg.default"
                      value={ruleForm.headerName}
                      onChange={(event) =>
                        setRuleForm({
                          ...ruleForm,
                          headerName: event.target.value,
                        })
                      }
                      placeholder={
                        ruleForm.matchType === 'header'
                          ? tRuleDialog('allHeadersIfEmpty')
                          : tRuleDialog('allParamsIfEmpty')
                      }
                    />
                  </Field.Root>
                ) : null}

                <HStack
                  gap="2"
                  alignItems="flex-end"
                  w="full"
                  flexWrap={replaceFieldConfig.hidden ? 'nowrap' : 'wrap'}
                >
                  <Field.Root flex="1" minW="0">
                    <Field.Label fontSize="xs" color="fg.muted">
                      {matchPatternConfig.label.replace(' *', '')}
                    </Field.Label>
                    <Input
                      size="xs"
                      h={RULE_EDITOR_CONTROL_HEIGHT}
                      fontSize="xs"
                      bg="bg.default"
                      value={ruleForm.matchPattern}
                      onChange={(event) =>
                        handleMatchPatternChange(event.target.value)
                      }
                      placeholder={matchPatternConfig.placeholder}
                    />
                  </Field.Root>

                  {!replaceFieldConfig.hidden ? (
                    <>
                      <Box pb="2" color="fg.muted" flexShrink={0}>
                        <ArrowRight02Icon size={14} />
                      </Box>
                      <Field.Root flex="1" minW="0">
                        <Field.Label fontSize="xs" color="fg.muted">
                          {replaceFieldConfig.label.replace(' *', '')}
                        </Field.Label>
                        <Input
                          size="xs"
                          h={RULE_EDITOR_CONTROL_HEIGHT}
                          fontSize="xs"
                          bg="bg.default"
                          value={ruleForm.replaceWith}
                          onChange={(event) =>
                            setRuleForm({
                              ...ruleForm,
                              replaceWith: event.target.value,
                            })
                          }
                          placeholder={replaceFieldConfig.placeholder}
                        />
                      </Field.Root>
                    </>
                  ) : null}
                </HStack>

                <HStack gap="3" w="full" flexWrap="wrap">
                  <Switch
                    size="sm"
                    checked={ruleForm.isRegex}
                    onCheckedChange={(details) =>
                      setRuleForm({ ...ruleForm, isRegex: details.checked })
                    }
                  >
                    <Text fontSize="2xs">{tRuleDialog('regex')}</Text>
                  </Switch>
                  <Switch
                    size="sm"
                    checked={ruleForm.caseSensitive}
                    onCheckedChange={(details) =>
                      setRuleForm({
                        ...ruleForm,
                        caseSensitive: details.checked,
                      })
                    }
                  >
                    <Text fontSize="2xs">{tRuleDialog('caseSensitive')}</Text>
                  </Switch>
                </HStack>
              </VStack>
            </Box>
          </RuleEditorSection>

          <Accordion.Root w="full" collapsible>
            <Accordion.Item value="conditions">
              <Accordion.ItemTrigger
                px="0"
                py="1"
                _hover={{ bg: 'transparent' }}
              >
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                  {tRuleDialog('sectionLimitScope')}
                </Text>
                <Accordion.ItemIndicator>
                  <ArrowDown01Icon size={12} />
                </Accordion.ItemIndicator>
              </Accordion.ItemTrigger>
              <Accordion.ItemContent pb="0">
                <VStack gap="2.5" alignItems="flex-start" w="full" pt="1">
                  <Field.Root w="full">
                    <Field.Label fontSize="xs" color="fg.muted">
                      {tRuleDialog('urlContains')}
                    </Field.Label>
                    <Input
                      size="xs"
                      h={RULE_EDITOR_CONTROL_HEIGHT}
                      fontSize="xs"
                      value={ruleForm.conditionUrlPattern}
                      onChange={(event) =>
                        setRuleForm({
                          ...ruleForm,
                          conditionUrlPattern: event.target.value,
                        })
                      }
                      placeholder={tRuleDialog('urlContainsPlaceholder')}
                    />
                  </Field.Root>

                  <Field.Root w="full">
                    <Field.Label fontSize="xs" color="fg.muted">
                      {tRuleDialog('methods')}
                    </Field.Label>
                    <ToggleGroup.Root
                      multiple
                      size="sm"
                      value={ruleForm.conditionMethods}
                      onValueChange={(details) => {
                        setRuleForm({
                          ...ruleForm,
                          conditionMethods: details.value,
                        });
                      }}
                    >
                      {HTTP_METHODS.map((method) => (
                        <ToggleGroup.Item
                          key={method}
                          value={method}
                          aria-label={method}
                          h="6"
                          px="2"
                          minW="52px"
                        >
                          <Text fontSize="2xs">{method}</Text>
                        </ToggleGroup.Item>
                      ))}
                    </ToggleGroup.Root>
                  </Field.Root>

                  <HStack gap="2" w="full" alignItems="flex-start">
                    <Field.Root flex="1" minW="0">
                      <Field.Label fontSize="xs" color="fg.muted">
                        {tRuleDialog('statusCodes')}
                      </Field.Label>
                      <Input
                        size="xs"
                        h={RULE_EDITOR_CONTROL_HEIGHT}
                        fontSize="xs"
                        value={ruleForm.conditionStatusCodes}
                        onChange={(event) =>
                          setRuleForm({
                            ...ruleForm,
                            conditionStatusCodes: event.target.value,
                          })
                        }
                        placeholder={tRuleDialog('statusCodesPlaceholder')}
                      />
                    </Field.Root>
                    <Field.Root flex="1" minW="0">
                      <Field.Label fontSize="xs" color="fg.muted">
                        {tRuleDialog('contentTypes')}
                      </Field.Label>
                      <Input
                        size="xs"
                        h={RULE_EDITOR_CONTROL_HEIGHT}
                        fontSize="xs"
                        value={ruleForm.conditionContentTypes}
                        onChange={(event) =>
                          setRuleForm({
                            ...ruleForm,
                            conditionContentTypes: event.target.value,
                          })
                        }
                        placeholder={tRuleDialog('contentTypesPlaceholder')}
                      />
                    </Field.Root>
                  </HStack>
                </VStack>
              </Accordion.ItemContent>
            </Accordion.Item>
          </Accordion.Root>

          <Field.Root w="full">
            <Field.Label fontSize="xs" color="fg.muted">
              {tRuleDialog('ruleName')}
            </Field.Label>
            <Input
              size="xs"
              h={RULE_EDITOR_CONTROL_HEIGHT}
              fontSize="xs"
              value={ruleForm.name}
              onChange={(event) =>
                setRuleForm({ ...ruleForm, name: event.target.value })
              }
              placeholder={suggestedName}
            />
          </Field.Root>
        </VStack>
      </Box>

      <HStack
        gap="2"
        justify="flex-end"
        w="full"
        px="3"
        py="2"
        borderTopWidth="1px"
        borderColor="border.subtle"
        bg="bg.subtle"
        flexShrink={0}
      >
        <Button
          variant="outline"
          size="xs"
          h={RULE_EDITOR_CONTROL_HEIGHT}
          onClick={onCancel}
        >
          {tCommon('cancel')}
        </Button>
        <Button
          size="xs"
          h={RULE_EDITOR_CONTROL_HEIGHT}
          onClick={onSave}
          disabled={!ruleForm.matchPattern.trim() || !ruleForm.applyTo.length}
        >
          {isEditing ? tRuleDialog('saveChanges') : tRuleDialog('createRule')}
        </Button>
      </HStack>
    </VStack>
  );
}
